import React, { useMemo } from "react";
import Select from "react-select";
import { motion } from "motion/react";
import moment from "moment";

import {
  capitalizeFirstLetter,
  mapAttributeToDisplayKey,
  pluralizeUnit,
} from "../../../../shared/helpers";
import { dosageEditLinkClass } from "../constants/viewPafConstants";
import { ToolTipHover } from "../components/ToolTipHover";
import { ViewPafDropdownIndicator } from "../components/ViewPafDropdownIndicator";
import {
  checkIfNumberOfCyclesExceeded,
  checkIfNumberOfWeeksExceeded,
  checkIfTotalCapsulesExceeded,
  getSortedCapsuleStrengthHistory,
} from "../utils/viewPafFormLogic";

/**
 * Dosage grid columns (strength, per-cycle, cycles/weeks, totals) for ViewPAF.
 * Kept in a hook so the main component file can focus on layout and data wiring.
 */
export function useViewPafDosageColumns({
  form,
  isRenewal,
  scheduleDisplayType,
  displayWeeks,
  prescriptionField,
  isFormApproved,
  showDosageFieldEdits,
  editingState,
  setEditingState,
  editing2State,
  setEditing2State,
  handlePrescriptionFields,
  handleEdit,
  textMotionProps,
  modifiedCapsuleStrengths,
  newStrengths,
  newCapsules,
  CapsulesperCycle,
  modifiedNCycles,
  modifiedWeeksCycles,
  modifiedWeeksPerCycle,
  oldValueType,
  oldValueWeeksPerCycle,
  validationContext,
  isPafEditMode = false,
}) {
  const strengthValueSort = useMemo(
    () => getSortedCapsuleStrengthHistory(form),
    [form]
  );

  const showDosageSelect = (isFieldEditing) =>
    isPafEditMode || Boolean(isFieldEditing);

  const showDosageEditLink = showDosageFieldEdits && !isPafEditMode;

  const closeDosageSelect = (updater) => {
    if (!isPafEditMode) {
      updater();
    }
  };

  const resolveOptionValue = (selected, options, displayValue) => {
    if (
      selected != null &&
      typeof selected === "object" &&
      (selected.id != null || selected.value != null || selected.label != null)
    ) {
      return selected;
    }
    if (!Array.isArray(options) || displayValue == null) return null;
    const str = String(displayValue).trim();
    const numeric = str.match(/\d+/)?.[0];
    return (
      options.find((o) => o.label === str) ??
      options.find((o) => String(o.label).trim() === str) ??
      (numeric
        ? options.find((o) => String(o.label).includes(numeric))
        : null) ??
      null
    );
  };

  const capsuleRenderer = (f, element, key) => {
    let result;

    if (isRenewal) {
      result = "Incomplete";
    }
    if (!isRenewal) {
      if (element.new_capsules === "Other (please state)") {
        result =
          element.new_capsules_other +
          ` ${pluralizeUnit(capitalizeFirstLetter(f?.unit_label || "capsule"))}`;
      } else if (element.new_capsules) {
        result =
          element.new_capsules +
          ` ${pluralizeUnit(capitalizeFirstLetter(f?.unit_label || "capsule"))}`;
      } else if (element.capsules !== "Incomplete") {
        result =
          element.capsules +
          ` ${pluralizeUnit(capitalizeFirstLetter(f?.unit_label || "capsule"))}`;
      } else {
        result = "Incomplete";
      }
    }
    const labelClass = result === "Incomplete" ? "red-label" : "";
    const greenText =
      !isRenewal &&
      f?.history?.capsules_ammends &&
      strengthValueSort[key]?.new_capsules
        ? "green-haze-color"
        : "";

    return (
      <span className={`${labelClass} ${greenText} font-size-16`}>{result}</span>
    );
  };

  const nCyclesRenderer = (f) => {
    let label;
    if (!!f?.number_cycles?.label && f?.number_cycles.id !== null) {
      label = f?.number_cycles.label;
    } else if (!!f?.new_capsule_cycles) {
      label = f?.new_capsule_cycles.label;
    } else {
      label = f?.n_cycles || "Incomplete";
    }
    const labelClass =
      label === "Incomplete" ||
      (scheduleDisplayType !== "weeks_per_cycle" &&
        checkIfNumberOfCyclesExceeded(f) === true)
        ? "red-label"
        : "";
    const greenText = !!f.new_capsule_cycles
      ? f.new_capsule_cycles
      : !!f?.n_cycles && !!f?.history?.n_cycles
        ? "green-haze-color"
        : "";

    return (
      <span className={`${labelClass}  ${greenText} font-size-16`}>{label}</span>
    );
  };

  const nWeeksRenderer = (f) => {
    let label;
    if (
      !!f?.new_capsule_cycles &&
      f?.new_capsule_cycles?.label !== "Other (Please State)"
    ) {
      label = f?.new_capsule_cycles?.label;
    } else if (!!f?.other) {
      label = f?.other + " Weeks";
    } else if (!!f?.number_weeks?.id) {
      label = f?.number_weeks?.id + " Weeks";
    } else if (!!f?.n_weeks_other) {
      label = f?.n_weeks_other + " Weeks";
    } else {
      label = f?.n_weeks || "Incomplete";
    }

    const greenText = !!f.new_capsule_cycles
      ? f.new_capsule_cycles
      : !!(f?.history?.n_weeks === 0
          ? f?.history?.n_weeks_other
          : f?.history?.n_weeks)
        ? "green-haze-color"
        : "";

    const labelClass =
      label === "Incomplete" ||
      checkIfNumberOfWeeksExceeded(f, validationContext) === true
        ? "red-label"
        : "";

    return (
      <span className={`${labelClass}  ${greenText} font-size-16`}>{label}</span>
    );
  };

  const weeksPerCycleRenderer = (f) => {
    let label;

    if (!!f?.new_weeks_per_cycle && f?.new_weeks_per_cycle?.id !== 0) {
      label = f?.new_weeks_per_cycle?.label;
    } else if (f?.new_weeks_per_cycle?.id === 0) {
      label = f?.new_weeks_per_cycle_other + " weeks";
    } else if (f?.weeks_per_cycle?.id === 0) {
      label = f?.other + " weeks";
    } else if (!!f?.weeks_per_cycle?.id) {
      label = f?.weeks_per_cycle?.id + " weeks";
    } else if (!!f?.weeks_per_cycle && f?.weeks_per_cycle?.id !== null) {
      label = f?.weeks_per_cycle + " weeks";
    } else {
      label = "Incomplete";
    }

    const greenText = !!f.new_weeks_per_cycle
      ? f.new_weeks_per_cycle
      : !!f?.history?.weeks_per_cycle
        ? "green-haze-color"
        : "";

    const labelClass = label === "Incomplete" ? "red-label" : "";

    return (
      <span className={`${labelClass}  ${greenText} font-size-16`}>{label}</span>
    );
  };

  const renderCapsuleStrengthColumn = () => {
    if (!Array.isArray(form?.capsules)) {
      if (!!form?.strengths && form?.strengths?.length > 0) {
        return form?.strengths.map((element, index) => (
          <div className="row" key={index}>
            {showDosageSelect(editing2State.strengths[index]) ? (
              <div data-testid={`view-paf-strength-select-${index}`}>
                <Select
                  menuShouldScrollIntoView={false}
                  name="capsuleStrengths"
                  placeholder="Select"
                  classNamePrefix="margin-custom-select custom-select"
                  components={{
                    DropdownIndicator: ViewPafDropdownIndicator,
                    IndicatorSeparator: () => null,
                  }}
                  onChange={(selectedOption) => {
                    handlePrescriptionFields(
                      selectedOption,
                      element,
                      "capsule_strength"
                    );
                    closeDosageSelect(() => {
                      const newEditingStates = [...editing2State.strengths];
                      newEditingStates[index] = false;
                      setEditing2State({
                        ...editing2State,
                        strengths: newEditingStates,
                      });
                    });
                  }}
                  value={
                    newStrengths?.[index] ??
                    resolveOptionValue(
                      null,
                      modifiedCapsuleStrengths,
                      element?.strength_value != null
                        ? String(element.strength_value)
                        : null
                    )
                  }
                  options={modifiedCapsuleStrengths}
                />
              </div>
            ) : (
              <motion.span
                className="display-flex-tooltip font-size-16"
                {...textMotionProps(0.1)}
              >
                {!isRenewal && !form?.capsules ? (
                  <span
                    className={
                      !isRenewal &&
                      form?.history?.strength_ammends &&
                      strengthValueSort[index]?.new_strength_id
                        ? "green-haze-color font-size-16"
                        : "font-size-16"
                    }
                  >
                    {element?.strength_value} mg{" "}
                  </span>
                ) : (
                  index === 0 && (
                    <span className="red-label font-size-16">Incomplete </span>
                  )
                )}
                {!isRenewal &&
                  form?.history?.strength_ammends &&
                  strengthValueSort[index]?.new_strength_id && (
                    <ToolTipHover
                      quantity={" mg"}
                      name={form?.history?.pharmacy_user}
                      date={moment(form?.history?.ammend_date).format(
                        "DD / MM / YYYY"
                      )}
                      oldValue={
                        strengthValueSort[index]?.old_strenght_Value
                          ? strengthValueSort[index]?.old_strenght_Value
                          : strengthValueSort[index]?.strength_id
                      }
                      newValue={strengthValueSort[index]?.strength_value}
                    />
                  )}
                {showDosageEditLink && (
                  <span
                    className={dosageEditLinkClass}
                    onClick={() => handleEdit(index, "strengths")}
                  >
                    EDIT
                  </span>
                )}
              </motion.span>
            )}
            {!!element?.new_capsules &&
              !isFormApproved &&
              form?.schedule_display_type !== "weeks_per_cycle" &&
              element?.new_capsules === "Other (please state)" && (
                <div className="mb-5" style={{ paddingBottom: "50px" }}></div>
              )}
          </div>
        ));
      }
      if (!showDosageSelect(editingState.strengths)) {
        return (
          <motion.span {...textMotionProps(0.1)}>
            {!!form?.strengths?.strength_value ? (
              <span className="font-size-16">
                {form?.strengths?.strength_value} mg
              </span>
            ) : (
              <span className="red-label font-size-16">Incomplete</span>
            )}
            {showDosageEditLink && (
              <span
                className={dosageEditLinkClass}
                onClick={() =>
                  setEditingState({
                    ...editingState,
                    strengths: true,
                  })
                }
              >
                EDIT
              </span>
            )}
          </motion.span>
        );
      }
      return (
        <div data-testid="view-paf-strength-select">
          <Select
            menuShouldScrollIntoView={false}
            name="capsuleStrengths"
            placeholder="Select"
            classNamePrefix="margin-custom-select custom-select"
            components={{
              DropdownIndicator: ViewPafDropdownIndicator,
              IndicatorSeparator: () => null,
            }}
            onChange={(selectedOption) => {
              handlePrescriptionFields(selectedOption, "incomplete_strengths");
              closeDosageSelect(() => {
                setEditingState({
                  ...editingState,
                  strengths: false,
                });
              });
            }}
            value={newStrengths}
            options={modifiedCapsuleStrengths}
          />
        </div>
      );
    }
    if (form.capsules.length === 0) {
      return <span className="red-label font-size-16">Incomplete</span>;
    }
    return form.capsules
      ?.slice()
      .sort(
        (a, b) =>
          parseFloat(a?.capsuleStrength?.value) -
          parseFloat(b?.capsuleStrength?.value)
      )
      .map((element, key) => (
        <motion.div className="row" key={key} {...textMotionProps(0.1)}>
          {element?.capsuleStrength?.value ? (
            <span className="font-size-16">{element.capsuleStrength.value}</span>
          ) : (
            <span className="red-label font-size-16">Incomplete</span>
          )}
        </motion.div>
      ));
  };

  const renderCapsulePerCycleColumn = () => {
    if (scheduleDisplayType === "weeks_per_cycle") {
      return !showDosageSelect(editingState.number_weeks) ? (
        <motion.div
          className="display-flex-tooltip pt-0.5"
          {...textMotionProps(0.1)}
        >
          <span className="display-flex-tooltip">
            {weeksPerCycleRenderer(form)}
            {!!form?.weeks_per_cycle && !!form?.history?.weeks_per_cycle && (
              <ToolTipHover
                quantity={mapAttributeToDisplayKey(form?.schedule_display_type)}
                name={form?.history?.pharmacy_user}
                date={moment(form?.history?.ammend_date).format("DD / MM / YYYY")}
                oldValue={oldValueWeeksPerCycle || "Incomplete "}
                newValue={form?.weeks_per_cycle}
              />
            )}
          </span>
          {showDosageEditLink && (
            <span
              className={dosageEditLinkClass}
              onClick={() =>
                setEditingState({
                  ...editingState,
                  number_weeks: true,
                })
              }
            >
              EDIT
            </span>
          )}
        </motion.div>
      ) : (
        <div data-testid="view-paf-weeks-per-cycle-select">
          <Select
            menuShouldScrollIntoView={false}
            placeholder="Select"
            classNamePrefix="margin-custom-select custom-select"
            components={{
              DropdownIndicator: ViewPafDropdownIndicator,
              IndicatorSeparator: () => null,
            }}
            onChange={(selectedOption) => {
              handlePrescriptionFields(selectedOption, "weeks_per_cycle");
              closeDosageSelect(() => {
                setEditingState({
                  ...editingState,
                  number_weeks: false,
                });
              });
            }}
            value={resolveOptionValue(
              form?.new_weeks_per_cycle,
              modifiedWeeksPerCycle,
              form?.weeks_per_cycle
            )}
            options={modifiedWeeksPerCycle}
          />
        </div>
      );
    }

    return form?.strengths?.length === 0 ||
      !form?.strengths ||
      (isRenewal &&
        (!Array.isArray(form?.capsules) || form.capsules.length === 0)) ? (
      <span className="red-label font-size-16">Incomplete</span>
    ) : !Array.isArray(form?.capsules) || form.capsules.length === 0 ? (
      Array.isArray(form?.strengths) ? (
      form.strengths.map((element, key) => (
        <div className="row" key={key}>
          {showDosageSelect(editing2State.capsules[key]) ? (
            <div data-testid={`view-paf-capsules-select-${key}`}>
              <Select
                menuShouldScrollIntoView={false}
                placeholder="Select"
                classNamePrefix="margin-custom-select custom-select"
                components={{
                  DropdownIndicator: ViewPafDropdownIndicator,
                  IndicatorSeparator: () => null,
                }}
                onChange={(selectedOption) => {
                  handlePrescriptionFields(selectedOption, element, "capsules");
                  closeDosageSelect(() => {
                    const newEditingStates = [...editing2State.capsules];
                    newEditingStates[key] = false;
                    setEditing2State({
                      ...editing2State,
                      capsules: newEditingStates,
                    });
                  });
                }}
                value={
                  newCapsules?.[key] ??
                  resolveOptionValue(
                    null,
                    CapsulesperCycle,
                    element?.new_capsules ?? element?.capsules
                  )
                }
                options={CapsulesperCycle}
              />
            </div>
          ) : (
            <motion.span
              className="display-flex-tooltip"
              {...textMotionProps(0.1)}
            >
              {capsuleRenderer(form, element, key)}
              {!isRenewal &&
                form?.history?.capsules_ammends &&
                strengthValueSort[key]?.new_capsules && (
                  <ToolTipHover
                    quantity={` ${pluralizeUnit(
                      capitalizeFirstLetter(form?.unit_label || "capsule")
                    )}`}
                    name={form?.history?.pharmacy_user}
                    date={moment(form?.history?.ammend_date).format(
                      "DD / MM / YYYY"
                    )}
                    oldValue={
                      strengthValueSort[key]?.capsules !== null
                        ? strengthValueSort[key]?.capsules
                        : "Incomplete"
                    }
                    newValue={
                      strengthValueSort[key].is_new_other_capsules
                        ? strengthValueSort[key]?.new_capsules_other
                        : strengthValueSort[key]?.new_capsules
                    }
                  />
                )}
              {showDosageEditLink && (
                <span
                  className={dosageEditLinkClass}
                  onClick={() => handleEdit(key, "capsules")}
                >
                  EDIT
                </span>
              )}
            </motion.span>
          )}
        </div>
      ))) : (
        <span className="red-label font-size-16">Incomplete</span>
      )
    ) : (
      form.capsules
        ?.slice()
        .sort(
          (a, b) =>
            parseFloat(a?.capsuleStrength?.value) -
            parseFloat(b?.capsuleStrength?.value)
        )
        ?.map((element, key) => (
          <motion.div className="row" key={key} {...textMotionProps(0.1)}>
            {(() => {
              const perCycle = element?.label === 0 ? element?.other : element?.label;
              const cycles = form?.number_cycles?.id;
              const n = Number(perCycle) * Number(cycles);
              const isValid = Number.isFinite(n) && n > 0;

              return isValid ? (
                <span className="font-size-16">
                  {n}{" "}
                  {pluralizeUnit(
                    capitalizeFirstLetter(form?.unit_label || "capsule")
                  )}
                </span>
              ) : (
                <span className="red-label font-size-16">Incomplete</span>
              );
            })()}
          </motion.div>
        ))
    );
  };

  const renderNumberOfCyclesColumn = () => {
    if (scheduleDisplayType === "weeks_per_cycle") {
      return !showDosageSelect(editingState.n_cycles) ? (
        <motion.div
          className="display-flex-tooltip pt-0.5"
          {...textMotionProps(0.1)}
        >
          <span className="display-flex-tooltip">
            {nCyclesRenderer(form)}
            {(!!form?.n_cycles || !!form?.n_weeks) &&
              (!!form?.history?.n_cycles ||
                !!form?.history?.n_weeks ||
                !!form?.history?.n_weeks_other) && (
                <ToolTipHover
                  quantity={"cycles"}
                  name={form?.history?.pharmacy_user}
                  date={moment(form?.history?.ammend_date).format("DD / MM / YYYY")}
                  oldValue={oldValueType || "Incomplete "}
                  newValue={
                    displayWeeks
                      ? parseFloat(form?.n_weeks) ||
                        parseFloat(form?.n_weeks_other)
                      : parseFloat(form?.n_cycles)
                  }
                />
              )}
          </span>
          {showDosageEditLink && (
            <span
              className={dosageEditLinkClass}
              onClick={() =>
                setEditingState({
                  ...editingState,
                  n_cycles: true,
                })
              }
            >
              EDIT
            </span>
          )}
        </motion.div>
      ) : (
        <div data-testid="view-paf-cycles-select">
          <Select
            menuShouldScrollIntoView={false}
            placeholder="Select"
            classNamePrefix="margin-custom-select custom-select"
            components={{
              DropdownIndicator: ViewPafDropdownIndicator,
              IndicatorSeparator: () => null,
            }}
            onChange={(selectedOption) => {
              handlePrescriptionFields(selectedOption, "capsule_cycles");
              closeDosageSelect(() => {
                setEditingState({
                  ...editingState,
                  n_cycles: false,
                });
              });
            }}
            value={resolveOptionValue(
              form?.new_capsule_cycles,
              modifiedNCycles,
              form?.n_cycles
            )}
            options={modifiedNCycles}
          />
        </div>
      );
    }

    return !showDosageSelect(editingState.n_cycles) ? (
      <motion.div
        className="display-flex-tooltip pt-0.5"
        {...textMotionProps(0.1)}
      >
        <span className="display-flex-tooltip">
          {prescriptionField?.schedule_display_type === "weeks" ||
          displayWeeks ||
          form?.number_weeks?.label?.includes("Weeks")
            ? nWeeksRenderer(form)
            : nCyclesRenderer(form)}
          {(!!form?.n_cycles || !!form?.n_weeks) &&
            (!!form?.history?.n_cycles ||
              !!form?.history?.n_weeks ||
              !!form?.history?.n_weeks_other) && (
              <ToolTipHover
                quantity={mapAttributeToDisplayKey(form?.schedule_display_type)}
                name={form?.history?.pharmacy_user}
                date={moment(form?.history?.ammend_date).format("DD / MM / YYYY")}
                oldValue={oldValueType || "Incomplete "}
                newValue={
                  displayWeeks
                    ? parseFloat(form?.n_weeks) || parseFloat(form?.n_weeks_other)
                    : parseFloat(form?.n_cycles)
                }
              />
            )}
        </span>
        {showDosageEditLink && (
          <span
            className={dosageEditLinkClass}
            onClick={() =>
              setEditingState({
                ...editingState,
                n_cycles: true,
              })
            }
          >
            EDIT
          </span>
        )}
      </motion.div>
    ) : (
      <div data-testid={displayWeeks ? "view-paf-weeks-select" : "view-paf-cycles-select"}>
        <Select
          menuShouldScrollIntoView={false}
          placeholder="Select"
          classNamePrefix="margin-custom-select custom-select"
          components={{
            DropdownIndicator: ViewPafDropdownIndicator,
            IndicatorSeparator: () => null,
          }}
          onChange={(selectedOption) => {
            handlePrescriptionFields(selectedOption, "capsule_cycles");
            closeDosageSelect(() => {
              setEditingState({
                ...editingState,
                n_cycles: false,
              });
            });
          }}
          value={resolveOptionValue(
            form?.new_capsule_cycles,
            displayWeeks ? modifiedWeeksCycles : modifiedNCycles,
            displayWeeks ? form?.n_weeks ?? form?.n_weeks_other : form?.n_cycles
          )}
          options={displayWeeks ? modifiedWeeksCycles : modifiedNCycles}
        />
      </div>
    );
  };

  const renderTotalSupplyColumn = () => (
    <>
      <motion.div className="min-w-0 pt-0.5" {...textMotionProps(0.1)}>
        <span
          className={`font-size-16 ${
            checkIfTotalCapsulesExceeded(form, validationContext) && "red-label"
          }  ${showDosageFieldEdits && "align-self"}`}
        >
          {scheduleDisplayType === "weeks_per_cycle"
            ? form?.total_weeks_supply > 0
              ? `${form?.total_weeks_supply} weeks`
              : "Incomplete"
            : form?.new_totalCapsules > 0
              ? `${form?.new_totalCapsules} ${pluralizeUnit(
                  capitalizeFirstLetter(form?.unit_label || "capsule")
                )}`
              : form?.total_capsules > 0
                ? `${form?.total_capsules} ${pluralizeUnit(
                    capitalizeFirstLetter(form?.unit_label || "capsule")
                  )}`
                : "Incomplete"}
        </span>
      </motion.div>
      <motion.div className="min-w-0 pt-0.5" {...textMotionProps(0.1)}>
        <span className="font-size-16">
          {form?.new_totalCapsules > 0
            ? `${form?.new_totalCapsules} ${pluralizeUnit(
                capitalizeFirstLetter(form?.unit_label || "capsule")
              )}`
            : form?.total_capsules > 0
              ? `${form?.total_capsules} ${pluralizeUnit(
                  capitalizeFirstLetter(form?.unit_label || "capsule")
                )}`
              : "—"}
        </span>
      </motion.div>
    </>
  );

  return {
    renderCapsuleStrengthColumn,
    renderCapsulePerCycleColumn,
    renderNumberOfCyclesColumn,
    renderTotalSupplyColumn,
  };
}
